import React from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  Layers, BarChart3, Percent, PieChart, FileText, ChevronDown, Home,
  CheckCircle2, Circle, Clock, TrendingUp,
} from 'lucide-react';

/* ----------------------------------------------------------------------------
   Cold-email campaign landing pages — modeled on the DrawReady email
   leave-behind. The "app" card is a faithful, clean recreation of the real
   Budget & Progress screen (real data from a live single-family build).
---------------------------------------------------------------------------- */

const CORNER_IMG = 'https://images.unsplash.com/photo-1628012209120-d9db7abf7eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=700';
const HOME_IMG = 'https://images.unsplash.com/photo-1748063578185-3d68121b11ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';

const Logo = () => (
  <div className="flex items-center gap-3">
    <div className="w-14 h-14 rounded-2xl bg-blue-600 flex items-center justify-center shadow-md shadow-blue-600/20">
      <span className="text-white text-3xl font-black leading-none">D</span>
    </div>
    <span className="text-4xl sm:text-5xl font-black tracking-tight">
      <span className="text-slate-900">Draw</span><span className="text-blue-600">Ready</span>
    </span>
  </div>
);

const HeaderBar = ({ tagline }) => (
  <div className="relative">
    <div className="pointer-events-none absolute -top-10 -right-14 w-80 h-48 overflow-hidden rounded-bl-[64px] opacity-90 hidden sm:block">
      <img src={CORNER_IMG} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/30 to-white" />
    </div>
    <div className="relative flex items-start justify-between gap-4 pt-10">
      <Logo />
      <p className="text-right text-xs sm:text-sm font-bold tracking-[0.18em] text-slate-400 leading-snug max-w-[140px] mt-2">
        {tagline}
      </p>
    </div>
  </div>
);

const Copy = ({ greeting, intro, hook, value }) => (
  <div className="mt-10">
    <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="campaign-greeting">Hi {greeting},</h1>
    <p className="mt-6 text-xl text-slate-700 leading-relaxed">{intro}</p>
    <p className="mt-2 text-xl text-slate-700 leading-relaxed">
      {hook.pre}<span className="font-bold text-blue-600">{hook.bold}</span>
    </p>
    <p className="mt-6 text-xl text-slate-600 leading-relaxed max-w-2xl">{value}</p>
  </div>
);

const CTA = ({ label, sub }) => (
  <div className="mt-12 text-center">
    <a href="/signup" data-testid="campaign-cta"
       className="inline-flex items-center gap-3 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-2xl font-bold px-14 py-5 shadow-xl shadow-blue-600/25 transition">
      {label} <span aria-hidden>&rarr;</span>
    </a>
    <p className="mt-3 text-slate-400">{sub}</p>
  </div>
);

const SignOff = () => (
  <div className="mt-14">
    <p className="text-xl text-slate-700">Would love to hear what you think.</p>
    <p className="mt-2 text-xl font-bold text-slate-900">&mdash; DrawReady Team</p>
    <p className="mt-2 text-sm font-semibold tracking-wider text-slate-400 uppercase">
      Real projects. Real numbers. A clearer path to what&rsquo;s next.
    </p>
  </div>
);

const Shell = ({ tagline, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50/50 to-white py-6 sm:py-10 px-3 sm:px-6" data-testid="campaign-page">
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/70 px-6 sm:px-12 pb-14 overflow-hidden">
      <HeaderBar tagline={tagline} />
      {children}
    </div>
  </div>
);

const Donut = ({ pct, label = 'Complete' }) => {
  const r = 50, c = 2 * Math.PI * r;
  return (
    <div className="relative w-28 h-28">
      <svg viewBox="0 0 120 120" className="w-28 h-28 -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
        <circle cx="60" cy="60" r={r} fill="none" stroke="#2563eb" strokeWidth="12" strokeLinecap="round"
                strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-extrabold text-slate-900">{pct}%</span>
        <span className="text-xs text-slate-400">{label}</span>
      </div>
    </div>
  );
};

const STATUS = {
  Complete: { pill: 'bg-green-100 text-green-700', bar: 'bg-green-500' },
  'In Progress': { pill: 'bg-blue-100 text-blue-700', bar: 'bg-blue-500' },
  'Not Started': { pill: 'bg-slate-100 text-slate-500', bar: 'bg-slate-300' },
};

const Chip = ({ Icon, color, label, value }) => (
  <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
    <span className={`w-9 h-9 rounded-full flex items-center justify-center ${color}`}><Icon className="w-4 h-4" /></span>
    <div>
      <p className="text-[11px] text-slate-400 leading-tight">{label}</p>
      <p className="text-lg font-bold text-slate-900 leading-tight">{value}</p>
    </div>
  </div>
);

const PHASES = [
  ['1. Planning & Permitting', '$18,500', '$17,920', 'Complete', 100],
  ['2. Site & Foundation', '$52,300', '$48,125', 'Complete', 100],
  ['3. Framing', '$68,750', '$32,416', 'In Progress', 60],
  ['4. Rough-Ins', '$54,200', '$11,380', 'Not Started', 0],
  ['5. Drywall', '$38,100', '$0', 'Not Started', 0],
  ['6. Finishes', '$82,400', '$0', 'Not Started', 0],
  ['7. Exterior & Site', '$32,900', '$0', 'Not Started', 0],
  ['8. Final & Closeout', '$21,154', '$0', 'Not Started', 0],
];
const ALERTS = [
  ['25% of budget reached', 'done'],
  ['50% of budget reached', 'done'],
  ['75% of budget', 'next'],
  ['90% of budget', 'off'],
  ['100% of budget', 'off'],
];

function BudgetProgressCard() {
  return (
    <div className="mt-10 rounded-2xl bg-white ring-1 ring-slate-200 shadow-lg shadow-slate-900/5 p-5 sm:p-6" data-testid="app-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <img src={HOME_IMG} alt="1615 38th St SE" className="w-28 h-20 rounded-lg object-cover flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-xl font-bold text-slate-900">1615 38th St SE</p>
          <p className="text-slate-500 text-sm">Single Family Residence</p>
        </div>
        <div className="flex-1 flex items-center gap-3 sm:px-6">
          <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full" style={{ width: '41%' }} />
          </div>
          <span className="text-sm text-slate-500 whitespace-nowrap">41% Complete</span>
        </div>
        <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 bg-white">
          <FileText className="w-4 h-4" /> Export Report <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
        </button>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Chip Icon={Layers} color="bg-blue-100 text-blue-600" label="Total Budget" value="$368,304" />
        <Chip Icon={BarChart3} color="bg-blue-100 text-blue-600" label="Spent to Date" value="$122,566" />
        <Chip Icon={Percent} color="bg-purple-100 text-purple-600" label="Interest to Date" value="$8,420" />
        <Chip Icon={PieChart} color="bg-green-100 text-green-600" label="Remaining Budget" value="$245,738" />
      </div>

      <div className="mt-5 grid lg:grid-cols-[1fr_260px] gap-5">
        <div className="rounded-xl border border-slate-100 p-4">
          <p className="font-bold text-slate-900 mb-3">Budget &amp; Progress by Phase</p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-slate-400 text-left text-xs">
                <th className="font-medium pb-2">Phase</th>
                <th className="font-medium pb-2">Budget</th>
                <th className="font-medium pb-2">Actual</th>
                <th className="font-medium pb-2">Status</th>
                <th className="font-medium pb-2 w-32">Progress</th>
              </tr>
            </thead>
            <tbody>
              {PHASES.map(([name, budget, actual, status, pct]) => (
                <tr key={name} className="border-t border-slate-50">
                  <td className="py-2.5 text-slate-700">{name}</td>
                  <td className="py-2.5 text-slate-600">{budget}</td>
                  <td className="py-2.5 text-slate-600">{actual}</td>
                  <td className="py-2.5">
                    <span className={`inline-block rounded-md px-2 py-0.5 text-xs font-medium ${STATUS[status].pill}`}>{status}</span>
                  </td>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${STATUS[status].bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-xl border border-slate-100 p-4">
          <p className="font-bold text-slate-900 mb-3">Project Snapshot</p>
          <div className="flex justify-center"><Donut pct={41} /></div>
          <div className="mt-4 flex items-center gap-2 justify-center">
            <Home className="w-5 h-5 text-blue-500" />
            <div className="text-center">
              <p className="text-xs text-slate-400 leading-tight">Cost per SQ FT</p>
              <p className="font-bold text-slate-900 leading-tight">$184</p>
            </div>
          </div>
          <p className="font-bold text-slate-900 mt-4 mb-2 text-sm">Budget Alerts</p>
          <ul className="space-y-1.5 text-sm">
            {ALERTS.map(([label, state]) => (
              <li key={label} className="flex items-center gap-2">
                {state === 'done' ? <CheckCircle2 className="w-4 h-4 text-green-500" fill="#22c55e" stroke="#fff" />
                  : state === 'next' ? <Circle className="w-4 h-4 text-green-400" />
                  : <Circle className="w-4 h-4 text-slate-300" />}
                <span className={state === 'off' ? 'text-slate-400' : 'text-slate-600'}>{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

const PROJ = [
  ['Oct 2026', '$129,893', '$1,664', '$347,723', '$1,664'],
  ['Nov 2026', '$157,886', '$2,931', '$508,540', ''],
  ['Dec 2026', '$174,030', '$4,242', '$686,812', ''],
  ['Jan 2027', '$290,668', '$5,664', '$988,318', '$10,837'],
  ['Apr 2027', '—', '$7,248', '$1,301,374', '$19,868'],
];

function InterestCard() {
  return (
    <div className="mt-10 rounded-2xl bg-white ring-1 ring-slate-200 shadow-lg shadow-slate-900/5 p-5 sm:p-6" data-testid="app-card">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <img src={HOME_IMG} alt="1615 38th St SE" className="w-28 h-20 rounded-lg object-cover flex-shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-xl font-bold text-slate-900">1615 38th St SE</p>
          <p className="text-slate-500 text-sm">Single Family Residence &middot; Construction loan</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm font-semibold text-amber-700">
          <Clock className="w-4 h-4" /> Accrues only on what&rsquo;s drawn
        </span>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Chip Icon={Percent} color="bg-red-100 text-red-600" label="Interest Due" value="$15,484.89" />
        <Chip Icon={Clock} color="bg-amber-100 text-amber-600" label="Per Day" value="$87.63" />
        <Chip Icon={BarChart3} color="bg-blue-100 text-blue-600" label="Rate" value="6.789%" />
        <Chip Icon={Layers} color="bg-green-100 text-green-600" label="Owner Equity" value="$73,378" />
      </div>

      <div className="mt-5 rounded-xl border border-slate-100 p-4">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-5 h-5 text-indigo-600" />
          <p className="font-bold text-slate-900">Draw &amp; Interest Projection</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-slate-400 text-left text-xs">
              <th className="font-medium pb-2">Month</th>
              <th className="font-medium pb-2 text-right">Construction draw</th>
              <th className="font-medium pb-2 text-right">Interest accrued</th>
              <th className="font-medium pb-2 text-right">Balance</th>
              <th className="font-medium pb-2 text-right">Payment due</th>
            </tr>
          </thead>
          <tbody>
            {PROJ.map(([m, draw, int, bal, due]) => (
              <tr key={m} className="border-t border-slate-50">
                <td className="py-2.5 font-medium text-slate-700">{m}</td>
                <td className="py-2.5 text-right text-blue-700 font-medium">{draw}</td>
                <td className="py-2.5 text-right text-red-600">{int}</td>
                <td className="py-2.5 text-right text-slate-600">{bal}</td>
                <td className="py-2.5 text-right font-bold text-slate-900">{due}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-slate-400">Projected payoff at maturity shown before you commit — so holding costs never surprise you.</p>
      </div>
    </div>
  );
}

const CONTENT = {
  builders: {
    tagline: 'BUILT FOR DEVELOPERS & BUILDERS.',
    greeting: 'Builders & Developers',
    intro: 'I know there are a lot of construction softwares out there.',
    hook: { pre: 'If you aren’t happy with what you’re using, ', bold: 'give DrawReady a spin.' },
    value: 'It’s a simple way to see your budget and project progress in one place — without digging through spreadsheets or waiting on someone to send you an update.',
    card: BudgetProgressCard,
    cta: 'Test Drive DrawReady',
    sub: 'Click to explore a real project. No sales call required.',
  },
  lenders: {
    tagline: 'BUILT FOR CONSTRUCTION LENDERS.',
    greeting: 'Construction Lenders',
    intro: 'I know you fund a lot of construction loans.',
    hook: { pre: 'If chasing draws and documents is slowing you down, ', bold: 'give DrawReady a spin.' },
    value: 'See every borrower’s budget, draws, and what’s left — line by line, on one approval screen. Over-budget items are flagged before you fund. No spreadsheets. No guessing.',
    card: BudgetProgressCard,
    cta: 'See a Live Loan',
    sub: 'Click to walk a real project end to end. No sales call required.',
  },
  interest: {
    tagline: 'BUILT FOR DEVELOPERS & BUILDERS.',
    greeting: 'Builders',
    intro: 'Most builders find out their real interest cost after the job is done.',
    hook: { pre: 'DrawReady shows it to the day — ', bold: 'and projects your payoff.' },
    value: 'Interest accrues only on what you’ve actually drawn. See your daily cost, every future draw, and exactly what you’ll owe at maturity — before you commit.',
    card: InterestCard,
    cta: 'Test Drive DrawReady',
    sub: 'Click to see your real interest on a live project. No sales call required.',
  },
};

function CampaignPage({ slug, context }) {
  const c = CONTENT[slug] || CONTENT.builders;
  const [sp] = useSearchParams();
  const greeting = sp.get('hi') || c.greeting;
  const Card = c.card;
  return (
    <Shell tagline={c.tagline}>
      <Copy greeting={greeting} intro={c.intro} hook={c.hook} value={c.value} />
      <Card />
      <CTA label={c.cta} sub={c.sub} />
      <SignOff />
      {context && <p className="mt-6 text-xs text-slate-300 text-center">Prepared for {context}</p>}
    </Shell>
  );
}

function CampaignIndex() {
  const items = [
    { slug: 'builders', title: 'Builders & Developers', desc: 'Budget & progress in one place.' },
    { slug: 'lenders', title: 'Construction Lenders', desc: 'Every borrower’s budget on one approval screen.' },
    { slug: 'interest', title: 'Interest & Payoff', desc: 'Real interest to the day + projected payoff.' },
  ];
  return (
    <div className="min-h-screen bg-slate-100 px-6 py-16">
      <div className="max-w-3xl mx-auto bg-white rounded-xl ring-1 ring-slate-200 p-10">
        <Logo />
        <h1 className="mt-8 text-2xl font-bold text-slate-900">Email campaign pages</h1>
        <p className="text-slate-500 mt-1">
          Drop any link into a cold email. Add <code className="text-blue-600">?hi=Chicago friends</code> to personalize the greeting.
        </p>
        <div className="mt-6 grid gap-3">
          {items.map((it) => (
            <Link key={it.slug} to={`/c/${it.slug}`} data-testid={`campaign-link-${it.slug}`}
                  className="flex items-center justify-between rounded-xl border border-slate-200 px-5 py-4 hover:border-blue-300 hover:shadow-sm transition">
              <div>
                <p className="font-semibold text-slate-900">{it.title}</p>
                <p className="text-sm text-slate-500">{it.desc}</p>
              </div>
              <span className="text-blue-600 text-sm font-medium">/c/{it.slug} &rarr;</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Campaign() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const context = sp.get('for') || '';
  if (!slug) return <CampaignIndex />;
  return <CampaignPage slug={slug} context={context} />;
}
